import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authAPI } from '../../services/(adminapi)/authAPI'
import { updateAgentStatus } from '../../services/(adminapi)/messagesAPI'

const getStoredData = (key) => {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : null
  } catch (error) {
    console.error(`Error parsing ${key} from localStorage:`, error)
    return null
  }
}

// Async thunks
export const signUp = createAsyncThunk(
  'auth/signUp',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authAPI.signUp(userData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Sign up failed')
    }
  }
)

export const signIn = createAsyncThunk(
  'auth/signIn',
  async (credentials, { rejectWithValue }) => {
    try {
      console.log('AuthSlice: Attempting sign in with credentials:', credentials)
      const response = await authAPI.signIn(credentials)
      console.log('AuthSlice: Sign in API response:', response)
      console.log('AuthSlice: Response data:', response.data)
      
      // Store token and sessionToken in localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token)
        console.log('AuthSlice: Token stored in localStorage')
      }
      if (response.data.sessionToken) {
        localStorage.setItem('sessionToken', response.data.sessionToken)
        console.log('AuthSlice: SessionToken stored in localStorage')
      }
      
      return response.data
    } catch (error) {
      console.error('AuthSlice: Sign in API error:', error)
      console.error('AuthSlice: Error response:', error.response)
      console.error('AuthSlice: Error data:', error.response?.data)
      return rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Sign in failed')
    }
  }
)

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (_, { getState, rejectWithValue }) => {
    try {
      const role = getState()?.auth?.user?.role
      if (role === 'Agent') {
        try {
          await updateAgentStatus('offline')
        } catch (_) {
          /* ignore */
        }
      }
      const sessionToken = localStorage.getItem('sessionToken')
      if (sessionToken) {
        await authAPI.signOut(sessionToken)
      }
      return null
    } catch (error) {
      // Even if API call fails, we still want to clear local state
      console.error('AuthSlice: Sign out API error:', error)
      return rejectWithValue(error.response?.data?.message || 'Sign out failed')
    }
  }
)

export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getProfile()
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile')
    }
  }
)

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await authAPI.updateProfile(payload)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile')
    }
  }
)

export const updateProfilePicture = createAsyncThunk(
  'auth/updateProfilePicture',
  async (profile_picture, { rejectWithValue }) => {
    try {
      const response = await authAPI.updateProfilePicture(profile_picture)
      return response.data.profile_picture
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update picture')
    }
  }
)

const initialState = {
  user: getStoredData('user'),
  business: getStoredData('business'),
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCredentials: (state, action) => {
      const { user, token, business } = action.payload
      state.user = user
      state.token = token
      state.isAuthenticated = true
      if (business) {
        state.business = business
      }
      localStorage.setItem('token', token)
      if (user) {
        localStorage.setItem('user', JSON.stringify(user))
      }
      if (business) {
        localStorage.setItem('business', JSON.stringify(business))
      }
    },
    clearAuth: (state) => {
      state.user = null
      state.business = null
      state.token = null
      state.isAuthenticated = false
      state.isLoading = false
      state.error = null
      localStorage.removeItem('token')
      localStorage.removeItem('sessionToken')
      localStorage.removeItem('user')
      localStorage.removeItem('business')
    },
    setBusinessLogo: (state, action) => {
      if (state.business && action.payload) {
        state.business = { ...state.business, logo: action.payload }
        localStorage.setItem('business', JSON.stringify(state.business))
      }
    },
    setBusinessName: (state, action) => {
      if (state.business && action.payload !== undefined) {
        state.business = { ...state.business, name: action.payload }
        localStorage.setItem('business', JSON.stringify(state.business))
      }
    },
    setHumanChatEnabled: (state, action) => {
      if (state.business) {
        const on = action.payload
        state.business = {
          ...state.business,
          human_chat_enabled: on,
          human_agent_enabled: on
        }
        localStorage.setItem('business', JSON.stringify(state.business))
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Sign Up
      .addCase(signUp.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(signUp.fulfilled, (state, action) => {
        console.log('AuthSlice - signUp.fulfilled payload:', action.payload)
        state.isLoading = false
        state.user = action.payload.user
        state.token = action.payload.token
        state.isAuthenticated = true
        state.business = action.payload.business
        console.log('AuthSlice - business set to:', action.payload.business)
        localStorage.setItem('token', action.payload.token)
        if (action.payload.user) {
          localStorage.setItem('user', JSON.stringify(action.payload.user))
        }
        if (action.payload.business) {
          localStorage.setItem('business', JSON.stringify(action.payload.business))
        }
      })
      .addCase(signUp.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      // Sign In
      .addCase(signIn.pending, (state) => {
        state.isLoading = true
    
      })
      .addCase(signIn.fulfilled, (state, action) => {
        console.log('AuthSlice - signIn.fulfilled payload:', action.payload)
        state.isLoading = false
        state.user = action.payload.user
        state.token = action.payload.token
        state.isAuthenticated = true
        state.business = action.payload.business
        console.log('AuthSlice - business set to:', action.payload.business)
        localStorage.setItem('token', action.payload.token)
        if (action.payload.user) {
          localStorage.setItem('user', JSON.stringify(action.payload.user))
        }
        if (action.payload.business) {
          localStorage.setItem('business', JSON.stringify(action.payload.business))
        }
      })
      .addCase(signIn.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      // Sign Out
      .addCase(signOut.pending, (state) => {
        state.isLoading = true
      })
      .addCase(signOut.fulfilled, (state) => {
        state.isLoading = false
        state.user = null
        state.business = null
        state.token = null
        state.isAuthenticated = false
        localStorage.removeItem('token')
        localStorage.removeItem('sessionToken')
        localStorage.removeItem('user')
        localStorage.removeItem('business')
      })
      .addCase(signOut.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      // Fetch Profile
      .addCase(fetchProfile.pending, (state) => {
        state.error = null
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        if (action.payload.user) {
          state.user = { ...state.user, ...action.payload.user }
          localStorage.setItem('user', JSON.stringify(state.user))
        }
        if (action.payload.business) {
          state.business = { ...state.business, ...action.payload.business }
          localStorage.setItem('business', JSON.stringify(state.business))
        }
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        if (state.user && action.payload?.name !== undefined) {
          state.user = { ...state.user, name: action.payload.name }
          localStorage.setItem('user', JSON.stringify(state.user))
        }
      })
      .addCase(updateProfilePicture.fulfilled, (state, action) => {
        if (state.user && action.payload) {
          state.user = { ...state.user, profile_picture: action.payload }
          localStorage.setItem('user', JSON.stringify(state.user))
        }
      })
  },
})

export const { clearError, setCredentials, clearAuth, setBusinessLogo, setBusinessName, setHumanChatEnabled } = authSlice.actions
export default authSlice.reducer